// src/utils/validatePropertyUpload.js
// Validación previa de ARCHIVOS en el cliente (feedback instantáneo, evita subidas inútiles).
// NO reemplaza la validación del backend ni la de campos (validateForm en el formulario);
// se enfoca en tamaño, tipo y cantidad de fotos/documentos (ver README §5).
import {
  UPLOAD_LIMITS,
  ALLOWED_PHOTO_MIMES,
  ALLOWED_DOC_MIMES,
  ALLOWED_PHOTO_LABEL,
  ALLOWED_DOC_LABEL,
  bytesToMB,
} from '../constants/propertyUpload';

/**
 * Valida las listas de fotos y documentos antes de armar el FormData.
 * @param {{ photos?: File[], documents?: File[] }} params
 * @returns {{ valid: boolean, errors: Array<{field:string, code:string, message:string}> }}
 */
export function validatePropertyUpload({ photos = [], documents = [] } = {}) {
  const errors = [];

  // 1) Fotos: mínimo y máximo
  if (photos.length < UPLOAD_LIMITS.MIN_PHOTOS) {
    errors.push({
      field: 'photo',
      code: 'PHOTO_REQUIRED',
      message: `Subí al menos ${UPLOAD_LIMITS.MIN_PHOTOS} fotos (tenés ${photos.length}).`,
    });
  }
  if (photos.length > UPLOAD_LIMITS.MAX_PHOTOS) {
    errors.push({
      field: 'photo',
      code: 'TOO_MANY_PHOTOS',
      message: `Máximo ${UPLOAD_LIMITS.MAX_PHOTOS} fotos (tenés ${photos.length}).`,
    });
  }

  // 2) Documentos: máximo
  if (documents.length > UPLOAD_LIMITS.MAX_DOCUMENTS) {
    errors.push({
      field: 'documentation',
      code: 'TOO_MANY_DOCUMENTS',
      message: `Máximo ${UPLOAD_LIMITS.MAX_DOCUMENTS} documentos (tenés ${documents.length}).`,
    });
  }

  // 3) Tipo y tamaño de cada foto
  photos.forEach((file) => {
    if (file?.type && !ALLOWED_PHOTO_MIMES.includes(file.type)) {
      errors.push({
        field: 'photo',
        code: 'INVALID_FILE_TYPE',
        message: `"${file.name}" no es una imagen válida (${ALLOWED_PHOTO_LABEL}).`,
      });
    }
    if (file?.size > UPLOAD_LIMITS.MAX_FILE_SIZE_BYTES) {
      errors.push({
        field: 'photo',
        code: 'FILE_TOO_LARGE',
        message: `La imagen "${file.name}" pesa ${bytesToMB(file.size)} MB (máx. 30 MB).`,
      });
    }
  });

  // 4) Tipo y tamaño de cada documento
  documents.forEach((file) => {
    if (file?.type && !ALLOWED_DOC_MIMES.includes(file.type)) {
      errors.push({
        field: 'documentation',
        code: 'INVALID_FILE_TYPE',
        message: `"${file.name}" no es un documento válido (${ALLOWED_DOC_LABEL}).`,
      });
    }
    if (file?.size > UPLOAD_LIMITS.MAX_FILE_SIZE_BYTES) {
      errors.push({
        field: 'documentation',
        code: 'FILE_TOO_LARGE',
        message: `El documento "${file.name}" pesa ${bytesToMB(file.size)} MB (máx. 30 MB).`,
      });
    }
  });

  // 5) Payload total aproximado (50 MB)
  const totalBytes = [...photos, ...documents].reduce((acc, f) => acc + (f?.size || 0), 0);
  if (totalBytes > UPLOAD_LIMITS.MAX_TOTAL_PAYLOAD_BYTES) {
    errors.push({
      field: 'general',
      code: 'PAYLOAD_TOO_LARGE',
      message: `El envío total pesa ${bytesToMB(totalBytes)} MB (máx. 50 MB). Subí menos archivos por vez.`,
    });
  }

  return { valid: errors.length === 0, errors };
}
